import { ANIMATION_DIRECTION_SOURCE } from './map-scripts.generated';
// Serialize one self-contained factory so WebViews keep all of its helpers
// together, including after production minification.
function createRouteAnimationDirectionTools() {
    const getRouteAnimationScreenVector = (map, from, to, current = from) => {
        const longitude = (((to[0] - from[0] + 180) % 360 + 360) % 360) - 180;
        const latitude = to[1] - from[1];
        const span = Math.max(Math.abs(longitude), Math.abs(latitude));
        if (!span)
            return { dx: 0, dy: 0 };
        const currentLongitude = (((current[0] - from[0] + 180) % 360 + 360) % 360) - 180;
        const progress = Math.max(0, Math.min(1, Math.abs(longitude) >= Math.abs(latitude)
            ? currentLongitude / longitude : (current[1] - from[1]) / latitude));
        const step = Math.min(1, .01 / span);
        const before = Math.max(0, progress - step), after = Math.min(1, progress + step);
        // A long segment becomes an arc on the globe. Its endpoint chord is not
        // the tangent under the marker; project a small part around its position.
        const a = map.project([from[0] + longitude * before, from[1] + latitude * before]);
        const b = map.project([from[0] + longitude * after, from[1] + latitude * after]);
        return { dx: b.x - a.x, dy: b.y - a.y };
    };
    const isRouteAnimationAircraft = (iconUrl, typeName = '') => /airplane|aeroplane|aircraft|самол[её]т|перел[её]т|авиа|flight/i.test(String(iconUrl || '') + ' ' + typeName);
    const routeAnimationIconTransform = (angle, iconUrl, baseRotation) => {
        let path = String(iconUrl || '').split(/[?#]/)[0].toLowerCase();
        try {
            path = decodeURIComponent(path);
        }
        catch { /* Keep malformed URLs usable. */ }
        const filename = path.split('/').pop() || '';
        const westbound = Math.sin(angle * Math.PI / 180) < -0.000001;
        // Front-view artwork has no sideways nose: keep it standing on its wheels.
        const frontView = /\/point_travel\/(?:car|bus(?:_origin)?|taxi\d*|metro\d*|tram|ferry|hiking\d*)\.svg$/.test(path);
        if (frontView && baseRotation === undefined)
            return 'rotate(0deg)';
        const sideView = /^(?:car(?:[\d ()]*|sharing)|taxi\d*|bus|bicycle|bicyclesport|train\d*|metro\d*|tram|ship|boat\d*|ferry\d*|yacht\d*|scooter\w*|cablecar\d*|horseback_riding\d*|walk\d*|trekking\d*|run\d*|running_gx5ambc1xoaj_256)\.(?:png|svg|webp)$/.test(filename);
        // PNG route icons are usually pre-rotated north; SVG side views face right.
        const facesRight = /^(?:bicyclesport|run2|running_gx5ambc1xoaj_256|scooter|cablecar)\./.test(filename)
            || /^(?:bicycle|train\d*|metro\d*|tram|ship|ferry\d*|yacht\d*|scooter\w*)\.svg$/.test(filename);
        const orientation = baseRotation !== null && baseRotation !== void 0 ? baseRotation : (facesRight ? 90 : 0);
        const rotation = Math.round((angle - orientation) * 1000) / 1000;
        const wheelsOnLeft = /^car1 \(2\)\./.test(filename);
        const flip = wheelsOnLeft ? !westbound : westbound;
        return 'rotate(' + rotation + 'deg)' + (sideView && flip ? (orientation === 0 ? ' scaleX(-1)' : ' scaleY(-1)') : '');
    };
    const routeAnimationHeading = (previous, dx, dy, timestamp, aircraft = false) => {
        if (!Number.isFinite(dx) || !Number.isFinite(dy) || !Number.isFinite(timestamp)
            || Math.hypot(dx, dy) < 0.000000001)
            return previous || null;
        // Track heading is independent of the artwork's original orientation.
        const target = Math.atan2(dx, -dy) * 180 / Math.PI;
        if (!previous)
            return { angle: target, timestamp };
        const elapsed = Math.max(0, timestamp - previous.timestamp);
        // Unwrap around the current angle so crossing +/-180 never adds a full turn.
        const delta = (((target - previous.angle + 180) % 360 + 360) % 360) - 180;
        const response = delta * (1 - Math.exp(-elapsed / (aircraft ? 80 : 140)));
        // Dense zigzags and isolated reversed vertices must not cause instant flips.
        const maxStep = elapsed * 0.36;
        return {
            angle: previous.angle + Math.max(-maxStep, Math.min(maxStep, response)),
            timestamp,
        };
    };
    const updateRouteAnimationDirection = (icon, dx, dy, timestamp) => {
        if (!icon)
            return;
        const previous = icon._routeAnimationDirectionState;
        const next = routeAnimationHeading(previous, dx, dy, timestamp, Boolean(icon._routeAnimationFollowHeading) || isRouteAnimationAircraft(icon._routeAnimationIconUrl || icon.src));
        if (!next || next === previous)
            return;
        icon._routeAnimationDirectionState = next;
        icon.style.transformOrigin = 'center center';
        const transform = routeAnimationIconTransform(next.angle, icon._routeAnimationIconUrl || icon.src, icon._routeAnimationBaseRotation);
        if (icon.style.transform !== transform)
            icon.style.transform = transform;
    };
    return { getRouteAnimationScreenVector, routeAnimationHeading, isRouteAnimationAircraft, routeAnimationIconTransform, updateRouteAnimationDirection };
}
export const { getRouteAnimationScreenVector, routeAnimationHeading, isRouteAnimationAircraft, routeAnimationIconTransform, updateRouteAnimationDirection } = createRouteAnimationDirectionTools();
export const ROUTE_ANIMATION_DIRECTION_SCRIPT = `
const { getRouteAnimationScreenVector, routeAnimationHeading, isRouteAnimationAircraft, routeAnimationIconTransform, updateRouteAnimationDirection } = (${ANIMATION_DIRECTION_SOURCE})();
`;
